import { useCallback, useEffect, useRef } from 'react';

import { useEditorContext } from 'utils/useEditorContext';
import { useCurrentUser } from 'utils/user/useCurrentUser';

import { useDraftAiAgents } from './useDraftAiAgents';
import { useRunAiAgent } from './useRunAiAgent';

interface UseQuickAiDraftOptions {
  dixaConversationId: string;
  /** Called once the draft has been inserted into the editor. */
  onInserted?: () => void;
  /** Called when the draft could not be generated. */
  onError?: () => void;
  /** Whether to skip loading the AI agents. */
  skip?: boolean;
}

export interface UseQuickAiDraftResult {
  /** Generates a draft and inserts it into the editor. */
  generateDraft: () => void;
  /** Whether a default AI agent is set to draft with. */
  hasAgent: boolean;
  isLoading: boolean;
}

/**
 * A hook for generating a reply draft with the default AI agent and inserting
 * it into the editor, without any intermediate preview.
 */
export function useQuickAiDraft({
  dixaConversationId,
  onInserted,
  onError,
  skip,
}: UseQuickAiDraftOptions): UseQuickAiDraftResult {
  const { user } = useCurrentUser();
  const { insertBelow } = useEditorContext();
  const { defaultAgent, isLoading: isLoadingAgents } = useDraftAiAgents({
    skip,
  });

  const { runAgent, text, isLoading, isError, isSuccess } = useRunAiAgent(
    defaultAgent?.id ?? '',
    dixaConversationId,
    user.id,
  );

  // Latches that a started run still needs its text inserted.
  const awaitsInsertRef = useRef(false);

  const generateDraft = useCallback(() => {
    if (!defaultAgent || !dixaConversationId) {
      return;
    }

    awaitsInsertRef.current = true;
    runAgent();
  }, [defaultAgent, dixaConversationId, runAgent]);

  useEffect(() => {
    if (!awaitsInsertRef.current || isLoading) {
      return;
    }

    if (isSuccess) {
      awaitsInsertRef.current = false;

      if (text) {
        insertBelow(text);
        onInserted?.();
      } else {
        onError?.();
      }

      return;
    }

    if (isError) {
      awaitsInsertRef.current = false;
      onError?.();
    }
  }, [isLoading, isSuccess, isError, text, insertBelow, onInserted, onError]);

  return {
    generateDraft,
    hasAgent: !!defaultAgent,
    isLoading: isLoadingAgents || isLoading,
  };
}
